"use client";

import { useRef } from "react";

import useArrowGestures from "../variants/useArrowGestures";
import Checkbox from "@/components/design-system/checkbox";
import MenuDropdown from "@/components/design-system/menuDropdown";
import Pill from "@/components/design-system/pill";
import Tooltip from "@/components/design-system/tooltip";
import CaretLeftIcon from "public/next-public/common/icons/caret-regular-left.svg";
import CaretRightIcon from "public/next-public/common/icons/caret-regular-right.svg";
import SlidersIcon from "public/next-public/common/icons/sliders-regular.svg";
import SortIcon from "public/next-public/common/icons/sort-regular.svg";
import CloseIcon from "public/next-public/common/icons/x-close-icon.svg";
import {
  FILTERS,
  SORT_OPTIONS,
  isFilterAvailable,
  type SellerOptionFilter,
  type SellerOptionSort,
} from "./sellerOptions";
import useScrollEdges from "./useScrollEdges";
import type { SellerOptions } from "./useSellerOptions";
import styles from "./sellerOptionsControls.module.scss";

// Filters and sort change the list in place; none of them is a link or touches the URL, so search engines
// never see a filtered copy of the page.

/**
 * Filter pills for the table. Hovering explains a filter, how to remove it, or why it's unavailable. Below xl
 * the pills sit in one row under the label that scrolls sideways, with arrows at whichever end has more.
 */
export function FilterPills({ sellerOptions, filterOrder }: FilterControlProps) {
  const { offers, filters, context, toggleFilter } = sellerOptions;
  const pillsRef = useRef<HTMLUListElement>(null);
  const { canScrollStart, canScrollEnd, page } = useScrollEdges(pillsRef);
  const arrowGestures = useArrowGestures(pillsRef);

  // Pointer-only: keyboard users tab through the pills, and a focused pill scrolls itself into view.
  const scrollArrow = (direction: "start" | "end") => (
    <div
      className={`${styles.scrollArrow} ${direction === "start" ? styles.scrollArrowStart : styles.scrollArrowEnd}`}
      aria-hidden
      {...arrowGestures}
    >
      <span className={styles.arrowBacking}>
        <button
          type="button"
          tabIndex={-1}
          className={styles.arrowButton}
          onClick={() => page(direction)}
          data-seller-options-filters-scroll={direction}
        >
          {direction === "start" ? <CaretLeftIcon width={16} height={16} /> : <CaretRightIcon width={16} height={16} />}
        </button>
      </span>
      <span className={styles.fade} />
    </div>
  );

  return (
    <div className={styles.FilterPills} data-seller-options-filters="">
      <FiltersLabel count={filters.size} />
      <div className={styles.pillRow}>
        {canScrollStart && scrollArrow("start")}
        <ul ref={pillsRef} className={styles.pills}>
          {filterOrder.map((filter) => {
            const selected = filters.has(filter);
            const available = selected || isFilterAvailable(filter, offers, context);
            const { label, description, unavailableDescription } = FILTERS[filter];

            return (
              <li key={filter}>
                <Tooltip
                  text={selected ? "Remove filter" : available ? description : unavailableDescription}
                  tailPlacement="above"
                  anchorInline
                  describeChild
                >
                  <Pill
                    label={label}
                    selected={selected}
                    ariaPressed={selected}
                    ariaDisabled={!available}
                    trailingIcon={selected ? <CloseIcon aria-hidden /> : undefined}
                    onClick={() => toggleFilter(filter)}
                    data-seller-options-filter={filter}
                    data-seller-options-filter-state={filterState(selected, available)}
                  />
                </Tooltip>
              </li>
            );
          })}
        </ul>
        {canScrollEnd && scrollArrow("end")}
      </div>
    </div>
  );
}

/** The drawer's filters: a dropdown of checkboxes, since a narrow screen has no room for pills. */
export function FilterMenu({ sellerOptions, filterOrder }: FilterControlProps) {
  const { offers, filters, context, toggleFilter, clearFilters } = sellerOptions;

  return (
    <div className={styles.FilterMenu} data-seller-options-filters="">
      <MenuDropdown
        label={
          <span className={styles.filterMenuLabel}>
            <SlidersIcon width={16} height={16} aria-hidden />
            <span>
              Filters
              <FilterCount count={filters.size} />
            </span>
          </span>
        }
        className={filters.size > 0 ? styles.filterMenuApplied : undefined}
        menuClassName={styles.filterMenuPanel}
        closeOnSelect={false}
      >
        <ul className={styles.filterMenuList}>
          {filterOrder.map((filter) => {
            const selected = filters.has(filter);
            const available = selected || isFilterAvailable(filter, offers, context);

            return (
              <li
                key={filter}
                className={styles.filterMenuItem}
                data-seller-options-filter={filter}
                data-seller-options-filter-state={filterState(selected, available)}
              >
                <Checkbox
                  label={FILTERS[filter].label}
                  checked={selected}
                  disabled={!available}
                  onChange={() => toggleFilter(filter)}
                />
              </li>
            );
          })}
        </ul>
        <button
          type="button"
          className={styles.clearFilters}
          onClick={clearFilters}
          disabled={filters.size === 0}
          data-seller-options-clear-filters=""
        >
          Clear All Filters
        </button>
      </MenuDropdown>
    </div>
  );
}

export function SellerOptionsSort({ sort, onChange, disabled }: SellerOptionsSortProps) {
  const selected = SORT_OPTIONS.find((option) => option.value === sort) ?? SORT_OPTIONS[0];

  return (
    <div className={styles.SellerOptionsSort} data-seller-options-sort={sort}>
      <SortIcon className={styles.icon} width={16} height={16} aria-hidden />
      <span className={styles.sortLabel} aria-hidden>
        Sort by:
      </span>
      <MenuDropdown
        label={<span className={styles.sortValue}>{selected.label}</span>}
        ariaLabel={`Sort sellers by ${selected.label}`}
        options={SORT_OPTIONS}
        selectedValue={sort}
        color="brand"
        align="right"
        caretSize={16}
        disabled={disabled}
        className={styles.sortMenu}
        onSelect={(next) => onChange(next as SellerOptionSort)}
      />
    </div>
  );
}

export function NoMatchingSellers({ onClearFilters }: { onClearFilters: () => void }) {
  return (
    <div className={styles.NoMatchingSellers}>
      <p className={styles.noMatchesTitle}>No vendors meet this filter criteria</p>
      <p className={styles.noMatchesText}>
        Change your filters or{" "}
        <button
          type="button"
          className={styles.inlineButton}
          onClick={onClearFilters}
          data-seller-options-clear-filters=""
        >
          Clear All Filters
        </button>
      </p>
    </div>
  );
}

function FiltersLabel({ count }: { count: number }) {
  return (
    <span className={styles.filtersLabel}>
      <SlidersIcon className={styles.icon} width={16} height={16} aria-hidden />
      <span>
        Filters
        <FilterCount count={count} />:
      </span>
    </span>
  );
}

function FilterCount({ count }: { count: number }) {
  if (count === 0) {
    return null;
  }
  return (
    <span className={styles.count}>
      {" "}
      ({count})<span className={styles.srOnly}> applied</span>
    </span>
  );
}

function filterState(selected: boolean, available: boolean): "selected" | "available" | "unavailable" {
  return selected ? "selected" : available ? "available" : "unavailable";
}

interface FilterControlProps {
  sellerOptions: SellerOptions;
  filterOrder: SellerOptionFilter[];
}

interface SellerOptionsSortProps {
  sort: SellerOptionSort;
  onChange: (sort: SellerOptionSort) => void;
  /** No offers to sort. */
  disabled: boolean;
}
